import {
  DiscontinuationStatus,
  DiscountGroup,
  ProductGroupBase,
  Supplier,
} from '@retail/products/types';
import { Prices } from './Prices';
import {
  CustomerMarkupCondition,
  FreightCondition,
  MarkupOrPriceSuggestionCondition,
  PurchaseCondition,
  RoundingCondition,
} from '@retail/calculus/types';
import { AssortmentPricesDTO } from '../dto';
import { dateFromString } from '@retail/utils';
import { ItemPriority } from './AssortmentItemBase';
import { PriceRoundingBrand } from '@retail/rounding/types';
import { AssortmentItemTag } from './AssortmentTag';

export type PriceStatus =
  | 'PURCHASE_AGREEMENT_MISSING'
  | 'PURCHASE_PRICE_MISSING'
  | 'MARKUP_MISSING'
  | 'PRICE_SUGGESTION_PRICE_MISSING';

export interface UserName {
  familyName: string;
  givenName: string;
  iamId: string;
}

export interface AssortmentPricesProps {
  inDraft: boolean;
  mgSupplierItemId: number;
  mgItemNumber: number;
  finfoNumber?: number;
  nobbNumber?: number;
  primaryText: string;
  secondaryText?: string;
  supplier: Supplier;
  mainSupplierName?: string;
  productGroup: ProductGroupBase;
  discountGroup: DiscountGroup;
  primaryUnit: string;
  basePriceUnit: string;
  primaryGtin?: string;
  purchasePriceDate?: Date;
  futurePurchasePriceDate?: Date;
  costPriceDate?: Date;
  salesPriceDate?: Date;
  futureSalesPriceDate?: Date;
  priority: ItemPriority;
  isStocked: boolean;
  markupOrPriceSuggestionCoverageDegree?: number;
  coverageDegree?: number;
  futureCoverageDegree?: number;
  coverageDegreeSecondary?: number;
  coverageDegreeTertiary?: number;
  purchaseCondition?: PurchaseCondition;
  freightCondition?: FreightCondition;
  markupOrPriceSuggestionCondition?: MarkupOrPriceSuggestionCondition;
  roundingCondition?: RoundingCondition;
  customerMarkupCondition?: CustomerMarkupCondition;
  futurePurchaseCondition?: PurchaseCondition;
  futureMarkupOrPriceSuggestionCondition?: MarkupOrPriceSuggestionCondition;
  futureRoundingCondition?: RoundingCondition;
  futureCustomerMarkupCondition?: CustomerMarkupCondition;
  pricesExVat: Prices;
  pricesIncVat: Prices;
  statusCodes: PriceStatus;
  vatFactor: number;
  discontinuationDate?: Date;
  discontinuationStatus?: DiscontinuationStatus;
  supplierBasePrice?: number;
  supplierBasePriceDate?: Date;
  supplierRecommendedPriceIncVat?: number;
  supplierRecommendedPriceDate?: Date;
  assortmentTags?: AssortmentItemTag[];
}

export class AssortmentPrices {
  inDraft: boolean;
  mgSupplierItemId: number;
  mgItemNumber: number;
  finfoNumber?: number;
  nobbNumber?: number;
  primaryText: string;
  secondaryText?: string;
  supplier: Supplier;
  mainSupplierName?: string;
  productGroup: ProductGroupBase;
  discountGroup: DiscountGroup;
  primaryUnit: string;
  basePriceUnit: string;
  primaryGtin?: string;
  purchasePriceDate?: Date;
  futurePurchasePriceDate?: Date;
  costPriceDate?: Date;
  salesPriceDate?: Date;
  futureSalesPriceDate?: Date;
  priority: ItemPriority;
  isStocked: boolean;
  markupOrPriceSuggestionCoverageDegree?: number;
  coverageDegree?: number;
  futureCoverageDegree?: number;
  coverageDegreeSecondary?: number;
  coverageDegreeTertiary?: number;
  purchaseCondition?: PurchaseCondition;
  freightCondition?: FreightCondition;
  markupOrPriceSuggestionCondition?: MarkupOrPriceSuggestionCondition;
  roundingCondition?: RoundingCondition;
  customerMarkupCondition?: CustomerMarkupCondition;
  futurePurchaseCondition?: PurchaseCondition;
  futureMarkupOrPriceSuggestionCondition?: MarkupOrPriceSuggestionCondition;
  futureRoundingCondition?: RoundingCondition;
  futureCustomerMarkupCondition?: CustomerMarkupCondition;
  pricesExVat: Prices;
  pricesIncVat: Prices;
  statusCodes: PriceStatus;
  vatFactor: number;
  discontinuationDate?: Date;
  discontinuationStatus?: DiscontinuationStatus;
  supplierBasePrice?: number;
  supplierBasePriceDate?: Date;
  supplierRecommendedPriceIncVat?: number;
  supplierRecommendedPriceDate?: Date;
  assortmentTags?: AssortmentItemTag[];

  constructor({
    inDraft,
    mgSupplierItemId,
    mgItemNumber,
    finfoNumber,
    nobbNumber,
    primaryText,
    secondaryText,
    supplier,
    mainSupplierName,
    productGroup,
    discountGroup,
    primaryUnit,
    basePriceUnit,
    primaryGtin,
    purchasePriceDate,
    futurePurchasePriceDate,
    costPriceDate,
    salesPriceDate,
    futureSalesPriceDate,
    priority,
    isStocked,
    markupOrPriceSuggestionCoverageDegree,
    coverageDegree,
    futureCoverageDegree,
    coverageDegreeSecondary,
    coverageDegreeTertiary,
    purchaseCondition,
    freightCondition,
    markupOrPriceSuggestionCondition,
    customerMarkupCondition,
    futurePurchaseCondition,
    futureMarkupOrPriceSuggestionCondition,
    futureCustomerMarkupCondition,
    pricesExVat,
    pricesIncVat,
    statusCodes,
    vatFactor,
    discontinuationDate,
    discontinuationStatus,
    supplierBasePrice,
    supplierBasePriceDate,
    supplierRecommendedPriceIncVat,
    supplierRecommendedPriceDate,
    roundingCondition,
    futureRoundingCondition,
    assortmentTags,
  }: AssortmentPricesProps) {
    this.inDraft = inDraft;
    this.mgSupplierItemId = mgSupplierItemId;
    this.mgItemNumber = mgItemNumber;
    this.finfoNumber = finfoNumber;
    this.nobbNumber = nobbNumber;
    this.primaryText = primaryText;
    this.secondaryText = secondaryText;
    this.supplier = supplier;
    this.mainSupplierName = mainSupplierName;
    this.productGroup = productGroup;
    this.discountGroup = discountGroup;
    this.primaryUnit = primaryUnit;
    this.basePriceUnit = basePriceUnit;
    this.primaryGtin = primaryGtin;
    this.purchasePriceDate = purchasePriceDate;
    this.futurePurchasePriceDate = futurePurchasePriceDate;
    this.costPriceDate = costPriceDate;
    this.salesPriceDate = salesPriceDate;
    this.futureSalesPriceDate = futureSalesPriceDate;
    this.priority = priority;
    this.isStocked = isStocked;
    this.markupOrPriceSuggestionCoverageDegree = markupOrPriceSuggestionCoverageDegree;
    this.coverageDegree = coverageDegree;
    this.futureCoverageDegree = futureCoverageDegree;
    this.coverageDegreeSecondary = coverageDegreeSecondary;
    this.coverageDegreeTertiary = coverageDegreeTertiary;
    this.purchaseCondition = purchaseCondition;
    this.freightCondition = freightCondition;
    this.markupOrPriceSuggestionCondition = markupOrPriceSuggestionCondition;
    this.customerMarkupCondition = customerMarkupCondition;
    this.futurePurchaseCondition = futurePurchaseCondition;
    this.futureMarkupOrPriceSuggestionCondition = futureMarkupOrPriceSuggestionCondition;
    this.futureCustomerMarkupCondition = futureCustomerMarkupCondition;
    this.pricesExVat = pricesExVat;
    this.pricesIncVat = pricesIncVat;
    this.statusCodes = statusCodes;
    this.vatFactor = vatFactor;
    this.discontinuationDate = discontinuationDate;
    this.discontinuationStatus = discontinuationStatus;
    this.supplierBasePrice = supplierBasePrice;
    this.supplierBasePriceDate = supplierBasePriceDate;
    this.supplierRecommendedPriceIncVat = supplierRecommendedPriceIncVat;
    this.supplierRecommendedPriceDate = supplierRecommendedPriceDate;
    this.roundingCondition = roundingCondition;
    this.futureRoundingCondition = futureRoundingCondition;
    this.assortmentTags = assortmentTags;
  }

  isActiveBasedOnDiscontinuationStatus = (): boolean => {
    if (!this.discontinuationStatus) return true;
    return this.discontinuationStatus !== 'SOLD_OUT' && this.discontinuationStatus !== 'REMOVED';
  };

  usesMarkupCondition = (): boolean => {
    return (
      this.markupOrPriceSuggestionCondition?.priceSuggestionType === 'MARKUP' ||
      this.markupOrPriceSuggestionCondition?.coverageDegree != null
    );
  };

  hasAppliedRoundingCondition = (): boolean => {
    return this.pricesExVat.salesPrice != null && this.roundingCondition != null;
  };

  static fromDTO = (
    dto: AssortmentPricesDTO,
    selectedBrand: PriceRoundingBrand
  ): AssortmentPrices =>
    new AssortmentPrices({
      inDraft: dto.inDraft,
      mgItemNumber: dto.mgItemNumber,
      mgSupplierItemId: dto.mgSupplierItemId,
      finfoNumber: dto.finfoNumber,
      nobbNumber: dto.nobbNumber,
      primaryText: dto.primaryText,
      secondaryText: dto.secondaryText,
      supplier: Supplier.fromDto({ id: dto.supplierNumber, name: dto.supplierName }),
      mainSupplierName: dto.mainSupplierName,
      productGroup: ProductGroupBase.fromDto({
        name: dto.productGroupName,
        groupNumber: dto.productGroupNumber,
      }),
      discountGroup: DiscountGroup.fromDto({
        id: dto.discountGroupId,
        code: dto.discountGroupCode,
        name: dto.discountGroupName,
        mgSupplierId: dto.supplierNumber,
      }),
      primaryUnit: dto.primaryUnit,
      basePriceUnit: dto.basePriceUnit,
      primaryGtin: dto.primaryGtin,
      purchasePriceDate: dateFromString(dto.purchasePriceDate),
      futurePurchasePriceDate: dateFromString(dto.futurePurchasePriceDate),
      costPriceDate: dateFromString(dto.costPriceDate),
      salesPriceDate: dateFromString(dto.salesPriceDate),
      futureSalesPriceDate: dateFromString(dto.futureSalesPriceDate),
      priority: {
        prioritised: dto.prioritised,
        validFrom: new Date(dto.priorityValidFrom),
        validTo: dateFromString(dto.priorityValidTo),
        addedBy: dto.priorityAddedBy,
      },
      isStocked: dto.isStocked,
      markupOrPriceSuggestionCoverageDegree: dto.markupOrPriceSuggestionCoverageDegree,
      coverageDegree: dto.coverageDegree,
      futureCoverageDegree: dto.futureCoverageDegree,
      coverageDegreeSecondary: dto.coverageDegreeSecondary,
      coverageDegreeTertiary: dto.coverageDegreeTertiary,
      purchaseCondition: dto.purchaseCondition
        ? PurchaseCondition.fromDto(dto.purchaseCondition)
        : undefined,
      freightCondition: dto.freightCondition
        ? FreightCondition.fromDto(dto.freightCondition)
        : undefined,
      markupOrPriceSuggestionCondition: dto.markupOrPriceSuggestionCondition
        ? MarkupOrPriceSuggestionCondition.fromDto(dto.markupOrPriceSuggestionCondition)
        : undefined,
      roundingCondition: dto.roundingCondition
        ? RoundingCondition.fromDto(dto.roundingCondition, selectedBrand)
        : undefined,
      customerMarkupCondition: dto.customerMarkupCondition
        ? CustomerMarkupCondition.fromDto(dto.customerMarkupCondition)
        : undefined,
      futurePurchaseCondition: dto.futurePurchaseCondition
        ? PurchaseCondition.fromDto(dto.futurePurchaseCondition)
        : undefined,
      futureMarkupOrPriceSuggestionCondition: dto.futureMarkupOrPriceSuggestionCondition
        ? MarkupOrPriceSuggestionCondition.fromDto(dto.futureMarkupOrPriceSuggestionCondition)
        : undefined,
      futureRoundingCondition: dto.futureRoundingCondition
        ? RoundingCondition.fromDto(dto.futureRoundingCondition, selectedBrand)
        : undefined,
      futureCustomerMarkupCondition: dto.futureCustomerMarkupCondition
        ? CustomerMarkupCondition.fromDto(dto.futureCustomerMarkupCondition)
        : undefined,
      pricesExVat: Prices.fromDTO(dto.pricesExVat),
      pricesIncVat: Prices.fromDTO(dto.pricesIncVat),
      statusCodes: dto.statusCodes,
      vatFactor: dto.vatFactor,
      discontinuationDate: dateFromString(dto.discontinuationDate),
      discontinuationStatus: dto.discontinuationStatus,
      supplierBasePrice: dto.supplierBasePrice,
      supplierBasePriceDate: dateFromString(dto.supplierBasePriceDate),
      supplierRecommendedPriceIncVat: dto.supplierRecommendedPriceIncVat,
      supplierRecommendedPriceDate: dateFromString(dto.supplierRecommendedPriceDate),
      assortmentTags: dto.assortmentTags ? dto.assortmentTags.map(AssortmentItemTag.fromDTO) : [],
    });
}
